import { useEffect, useRef, useState } from 'react';
import {
  MOBILE_SUMMARY_CELL_PADDING,
  MOBILE_SUMMARY_RESIZE_DEBOUNCE_MS,
} from './constants';

//TODO: This resizeobserver pattern occurs in multiple places. it should be extracted into a hook
/**
 * Hook to track the width of the table container for mobile summary cell sizing.
 *
 * Uses ResizeObserver with debouncing to efficiently track container width
 * and calculate the appropriate summary cell width (container - padding).
 *
 * @param enabled - Whether to enable width tracking (typically `isMobile`)
 * @returns The calculated summary width, or null if disabled/unavailable
 */
export function useMobileSummaryWidth(enabled: boolean): number | null {
  const [summaryWidth, setSummaryWidth] = useState<number | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled) {
      setSummaryWidth(null);
      return;
    }

    const container = document.querySelector('[data-slot="table-container"]');
    if (!container) {
      if (import.meta.env.DEV) {
        console.warn(
          '[useMobileSummaryWidth] table-container not found. ' +
            'Ensure the Table component has data-slot="table-container".',
        );
      }
      return;
    }

    const updateWidth = (width: number) => {
      // Guard against negative widths
      setSummaryWidth(Math.max(0, width - MOBILE_SUMMARY_CELL_PADDING));
    };

    observerRef.current = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      // Debounce updates to prevent excessive re-renders during resize
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        updateWidth(entry.contentRect.width);
      }, MOBILE_SUMMARY_RESIZE_DEBOUNCE_MS);
    });

    observerRef.current.observe(container);

    // Set initial width
    const rect = container.getBoundingClientRect();
    updateWidth(rect.width);

    return () => {
      observerRef.current?.disconnect();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [enabled]);

  return summaryWidth;
}
